import { Lock } from 'lucide-react';
import {
  daemonSyncSchema,
  hasPermission,
  startupViewSchema,
  validateVariable,
  type StartupVariable,
  type StartupView,
} from '@hearth/shared';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiRequestError, apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery, startupQuery } from '@/lib/server-queries';
import {
  groupVariables,
  isOn,
  variableInput,
  type VariableGroup,
  type VariableInput,
} from '@/lib/variable-input';

export const Route = createFileRoute('/_app/server/$server/startup')({
  component: StartupTab,
});

const savedSchema = startupViewSchema.extend(daemonSyncSchema.shape);
type Saved = StartupView & { daemonSynced: boolean; daemonError: string | null };

const GROUP_DESCRIPTIONS: Record<VariableGroup['id'], (game: string) => string> = {
  version: (game) =>
    `Which version of ${game} runs. Changes apply the next time the server starts; back up first when changing versions.`,
  settings: () => 'Changes are saved one at a time and apply the next time the server starts.',
  locked: () => 'Only an administrator can change these.',
};

function StartupTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const { data: startup } = useSuspenseQuery(startupQuery(id));
  const queryClient = useQueryClient();
  const canEdit = hasPermission(server.permissions, 'startup.update');
  const canPickImage = hasPermission(server.permissions, 'startup.docker-image');

  const applied = (res: Saved) => {
    queryClient.setQueryData(startupQuery(id).queryKey, res);
    if (!res.daemonSynced)
      toast.warning(`Saved, but the node didn't get the change yet: ${res.daemonError}`);
    else toast.success('Saved. It applies the next time the server starts.');
  };

  const image = useMutation({
    mutationFn: (value: string) =>
      apiRequest(`/api/client/servers/${id}/startup/docker-image`, {
        method: 'PUT',
        body: { image: value },
        schema: savedSchema,
      }),
    onSuccess: (res) => {
      applied(res);
      void queryClient.invalidateQueries({ queryKey: serverQuery(id).queryKey });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const groups = groupVariables(startup.variables);
  const images = Object.entries(startup.dockerImages);
  // Show the current image even when the egg no longer lists it.
  const current = images.find(([, ref]) => ref === startup.image);
  const rowProps = { server: id, canEdit, onSaved: applied };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Docker image</CardTitle>
          <CardDescription>
            The runtime the server starts in, for example a different Java version.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2">
          <Select
            value={startup.image}
            disabled={!canPickImage || image.isPending}
            onValueChange={(v) => image.mutate(v)}
          >
            <SelectTrigger className="w-full max-w-lg" aria-label="Docker image">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!current && <SelectItem value={startup.image}>{startup.image}</SelectItem>}
              {images.map(([label, ref]) => (
                <SelectItem key={ref} value={ref}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground font-mono text-xs break-all">{startup.image}</p>
        </CardContent>
      </Card>

      <Tabs defaultValue="guided" className="gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold">Startup settings</h2>
          <TabsList aria-label="How to show the startup settings">
            <TabsTrigger value="guided">Guided</TabsTrigger>
            <TabsTrigger value="raw">Raw</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="guided" className="grid gap-6">
          {groups.length === 0 && (
            <Card>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  This server has no variables you can see.
                </p>
              </CardContent>
            </Card>
          )}
          {groups.map((g) => (
            <Card key={g.id}>
              <CardHeader>
                <CardTitle>{g.label}</CardTitle>
                <CardDescription>{GROUP_DESCRIPTIONS[g.id](server.egg.name)}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6">
                {g.variables.map((v) => (
                  <GuidedVariable key={`${v.envVariable}:${v.value}`} variable={v} {...rowProps} />
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="raw" className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Startup command</CardTitle>
              <CardDescription>
                What the server runs when it starts. Variables you can’t see are shown as they are
                written.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              <pre
                className="bg-muted overflow-x-auto rounded-md p-3 font-mono text-sm break-all whitespace-pre-wrap"
                aria-label="Resolved startup command"
              >
                {startup.renderedCommand}
              </pre>
              {startup.startupCommand !== startup.renderedCommand && (
                <details className="text-sm">
                  <summary className="text-muted-foreground cursor-pointer text-xs">
                    With variable placeholders
                  </summary>
                  <pre
                    className="bg-muted mt-2 overflow-x-auto rounded-md p-3 font-mono text-xs break-all whitespace-pre-wrap"
                    aria-label="Startup command template"
                  >
                    {startup.startupCommand}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Variables</CardTitle>
              <CardDescription>
                Every variable as plain text, with the rules the panel checks it against.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6">
              {startup.variables.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  This server has no variables you can see.
                </p>
              )}
              {startup.variables.map((v) => (
                <RawVariable key={`${v.envVariable}:${v.value}`} variable={v} {...rowProps} />
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface RowProps {
  server: string;
  variable: StartupVariable;
  canEdit: boolean;
  onSaved: (res: Saved) => void;
}

/** Saves one variable; a field error from the API comes back as `error`. */
function useSaveVariable({ server, variable, onSaved }: RowProps) {
  const [error, setError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (value: string) =>
      apiRequest(`/api/client/servers/${server}/startup/variable`, {
        method: 'PUT',
        body: { key: variable.envVariable, value },
        schema: savedSchema,
      }),
    onSuccess: (res) => {
      setError(undefined);
      onSaved(res);
    },
    onError: (e) => {
      const field = e instanceof ApiRequestError ? e.fieldErrors('body.value')[0] : undefined;
      if (field) setError(field);
      else toast.error(errorMessage(e));
    },
  });
  return { save, error, setError };
}

function Placeholder({ variable }: { variable: StartupVariable }) {
  return <code className="font-mono">{`{{${variable.envVariable}}}`}</code>;
}

function Description({ variable, rules }: { variable: StartupVariable; rules?: boolean }) {
  return (
    <>
      {variable.description} <Placeholder variable={variable} />
      {rules && variable.rules && (
        <>
          {' · '}
          <code className="font-mono">{variable.rules}</code>
        </>
      )}
      {!variable.isEditable && ' · only an administrator can change this'}
    </>
  );
}

/** A variable rendered as the control its rules call for. */
function GuidedVariable(props: RowProps) {
  const { variable, canEdit } = props;
  const editable = canEdit && variable.isEditable;
  const { input, required } = variableInput(variable);
  if (input.kind === 'boolean')
    return <BooleanVariable {...props} input={input} editable={editable} />;
  if (input.kind === 'select')
    return <SelectVariable {...props} input={input} editable={editable} required={required} />;
  return <TextVariable {...props} input={input} editable={editable} required={required} />;
}

function BooleanVariable(
  props: RowProps & { input: Extract<VariableInput, { kind: 'boolean' }>; editable: boolean },
) {
  const { variable, input, editable } = props;
  const { save, error } = useSaveVariable(props);
  const id = useId();
  const on = isOn(input, save.isPending ? (save.variables ?? variable.value) : variable.value);
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center gap-3">
        <Switch
          id={id}
          checked={on}
          disabled={!editable || save.isPending}
          onCheckedChange={(v) => save.mutate(v ? input.on : input.off)}
          aria-describedby={`${id}-desc`}
          aria-invalid={error ? true : undefined}
        />
        <Label htmlFor={id}>{variable.name}</Label>
        {!editable && <Lock className="text-subtle size-3.5" aria-label="Read-only" />}
      </div>
      <p id={`${id}-desc`} className="text-muted-foreground text-xs">
        <Description variable={variable} />
      </p>
      {error && (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function SelectVariable(
  props: RowProps & {
    input: Extract<VariableInput, { kind: 'select' }>;
    editable: boolean;
    required: boolean;
  },
) {
  const { variable, input, editable, required } = props;
  const { save, error } = useSaveVariable(props);
  // A value outside the list (set before the rules changed) is still shown as it is.
  const options =
    variable.value && !input.options.includes(variable.value)
      ? [variable.value, ...input.options]
      : input.options;
  const value = save.isPending ? (save.variables ?? variable.value) : variable.value;
  return (
    <div className="max-w-lg">
      <FormField
        label={variable.name}
        error={error}
        description={<Description variable={variable} />}
      >
        <Select
          value={value || undefined}
          disabled={!editable || save.isPending}
          onValueChange={(v) => save.mutate(v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={required ? 'Choose one' : 'Not set'} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    </div>
  );
}

function TextVariable(
  props: RowProps & {
    input: Extract<VariableInput, { kind: 'number' | 'text' }>;
    editable: boolean;
    required: boolean;
  },
) {
  const { variable, input, editable, required } = props;
  const { save, error, setError } = useSaveVariable(props);
  const [value, setValue] = useState(variable.value);
  // The panel checks the same rules on save; checking here explains a problem before that.
  const problem =
    value !== variable.value ? validateVariable(variable.name, value, variable.rules) : null;
  const dirty = value !== variable.value;
  const attrs =
    input.kind === 'number'
      ? {
          type: 'number',
          inputMode: 'decimal' as const,
          min: input.min,
          max: input.max,
          step: input.step,
        }
      : { type: 'text', minLength: input.minLength, maxLength: input.maxLength };
  return (
    <form
      className="flex max-w-xl flex-wrap items-start gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem) save.mutate(value);
      }}
    >
      <div className="min-w-0 flex-1 basis-64">
        <FormField
          label={variable.name}
          error={error ?? problem ?? undefined}
          description={<Description variable={variable} />}
        >
          <Input
            {...attrs}
            value={value}
            readOnly={!editable}
            required={required}
            onChange={(e) => {
              setValue(e.target.value);
              setError(undefined);
            }}
            className="font-mono"
          />
        </FormField>
      </div>
      {editable ? (
        <Button
          type="submit"
          className="sm:mt-[1.375rem]"
          disabled={!dirty || !!problem || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      ) : (
        <Lock className="text-subtle mt-8 size-3.5" aria-label="Read-only" />
      )}
    </form>
  );
}

/** A variable as plain text, whatever its rules; the escape hatch when guided gets it wrong. */
function RawVariable(props: RowProps) {
  const { variable, canEdit } = props;
  const { save, error, setError } = useSaveVariable(props);
  const [value, setValue] = useState(variable.value);
  const editable = canEdit && variable.isEditable;
  const dirty = value !== variable.value;
  return (
    <form
      className="flex max-w-xl flex-wrap items-start gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(value);
      }}
    >
      <div className="min-w-0 flex-1 basis-64">
        <FormField
          label={variable.name}
          error={error}
          description={<Description variable={variable} rules />}
        >
          <Input
            value={value}
            readOnly={!editable}
            onChange={(e) => {
              setValue(e.target.value);
              setError(undefined);
            }}
            className="font-mono"
          />
        </FormField>
      </div>
      {editable ? (
        <Button type="submit" className="sm:mt-[1.375rem]" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      ) : (
        <Lock className="text-subtle mt-8 size-3.5" aria-label="Read-only" />
      )}
    </form>
  );
}
